// Editor-HTML → Mail-HTML: jeder Absatz, jede Zelle und jeder Listenpunkt trägt Schrift/Größe/Farbe inline,
// weil Outlook (Word-Engine) und viele Webmailer <style>-Blöcke und Vererbung nicht zuverlässig anwenden.

import { BASE_TEXT_STYLE } from './houseStyle';

export const BODY_START = '<!-- bodyContentStart -->';
export const BODY_END = '<!-- bodyContentEnd -->';

const PARAGRAPH_STYLE = 'margin:0;' + BASE_TEXT_STYLE;
// Zeilenabstand der bisherigen Mails (Signatur-Hülle in TopPart: line-height:12.0pt). Nicht bei größerer Schrift
// oder Bildern – feste Zeilenhöhe würde dort in Outlook abschneiden.
const LINE_HEIGHT = 'line-height:12.0pt';

function fitsFixedLineHeight(block) {
    if (block.querySelector('img, [data-twig]')) {
        return false;
    }
    return !Array.from(block.querySelectorAll('[style*="font-size"]')).some(el => {
        const match = (el.getAttribute('style') || '').match(/font-size\s*:\s*([\d.]+)pt/i);
        return match && parseFloat(match[1]) > 10;
    });
}

function mergeStyle(base, own) {
    const declarations = (own || '').split(';').map(item => item.trim()).filter(Boolean);
    return [base, ...declarations].filter(Boolean).join(';');
}

// Inhalt der Massenemail-Bausteine ist gerenderte Vorschau und wird beim Versand durch Twig ersetzt – nicht anfassen
// Tabellen (data-email-block, siehe HtmlBlock) gehen unverändert in die Mail
function outsideTokens(root, selector) {
    return Array.from(root.querySelectorAll(selector)).filter(el => !el.closest('[data-twig], [data-email-block]'));
}

function endsWithBreak(block) {
    let node = block.lastChild;
    while (node && node.nodeType === 3 && /^[ \t\r\n]*$/.test(node.textContent)) {
        node = node.previousSibling;
    }
    return !!node && node.nodeType === 1 && node.tagName === 'BR';
}

function isEmptyParagraph(p) {
    return !p.textContent.replace(/[\s\u00a0]/g, '') && !p.querySelector('img, [data-twig]');
}

export function serializeEmailBody(editorHtml) {
    if (!editorHtml) {
        return '';
    }
    const doc = new DOMParser().parseFromString(`<body>${editorHtml}</body>`, 'text/html');
    const body = doc.body;

    // Listenpunkte: <li><p>Text</p></li> → <li>Text</li>, sonst setzt Outlook Absatzabstände in die Liste
    outsideTokens(body, 'li').forEach(li => {
        const paragraphs = Array.from(li.children).filter(child => child.tagName === 'P');
        if (paragraphs.length === 1 && li.firstElementChild === paragraphs[0]) {
            const p = paragraphs[0];
            const align = p.style.textAlign;
            while (p.firstChild) {
                li.insertBefore(p.firstChild, p);
            }
            p.remove();
            if (align) {
                li.style.textAlign = align;
            }
        }
        li.setAttribute('style', mergeStyle('margin:0;' + BASE_TEXT_STYLE + (fitsFixedLineHeight(li) ? ';' + LINE_HEIGHT : ''), li.getAttribute('style')));
    });
    outsideTokens(body, 'ul, ol').forEach(list => {
        // gleicher Einzug für Aufzählung und Nummerierung – Handy-Mailprogramme rücken ul/ol sonst verschieden ein
        // (Outlook Desktop ignoriert padding an Listen und rückt selbst ein)
        list.setAttribute('style', 'margin-top:0;margin-bottom:0;padding-left:30px');
    });

    outsideTokens(body, 'p').forEach(p => {
        if (isEmptyParagraph(p) && !p.querySelector('br')) {
            p.innerHTML = '&nbsp;';
        } else if (endsWithBreak(p)) {
            // Umschalt+Enter am Zeilenende: im Editor eine Leerzeile, Outlook würde das letzte <br> verschlucken
            p.appendChild(doc.createTextNode('\u00a0'));
        }
        p.setAttribute('class', 'MsoNormal');
        p.setAttribute('style', mergeStyle(PARAGRAPH_STYLE + (fitsFixedLineHeight(p) ? ';' + LINE_HEIGHT : ''), p.getAttribute('style')));
    });

    // TextStyle ohne Attribute erzeugt leere <span>
    outsideTokens(body, 'span').forEach(span => {
        if (!span.getAttribute('style')) {
            while (span.firstChild) {
                span.parentNode.insertBefore(span.firstChild, span);
            }
            span.remove();
        }
    });

    outsideTokens(body, 'img').forEach(img => img.setAttribute('border', '0'));

    // der Browser schreibt Farben als rgb(); Outlook (Word-Engine) versteht Hex zuverlässiger
    outsideTokens(body, '[style]').forEach(el => {
        el.setAttribute('style', el.getAttribute('style').replace(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*[\d.]+\s*)?\)/g,
            (match, r, g, b) => '#' + [r, g, b].map(n => parseInt(n, 10).toString(16).padStart(2, '0')).join('').toUpperCase()));
    });

    body.querySelectorAll('[data-email-block]').forEach(el => el.removeAttribute('data-email-block'));

    return body.innerHTML;
}

// Vorlage aus TopPart/EmailBody: Start-Marker, dann Hülle <p><a name="_MailAutoSig"><span …>, Text, </span></p>, End-Marker
const SIGNATURE_WRAPPER_START = /^\s*<p\b[^>]*>\s*<a\s+name="?_MailAutoSig"?\s*>\s*<span\b[^>]*>/i;
const SIGNATURE_WRAPPER_END = /<\/span>\s*(<\/a>\s*)?<\/p>\s*$/i;

export function splitEmailDocument(documentHtml) {
    if (!documentHtml) {
        return null;
    }
    const start = documentHtml.indexOf(BODY_START);
    const end = start >= 0 ? documentHtml.indexOf(BODY_END, start) : -1;
    if (start < 0 || end < 0) {
        return null;
    }
    let inner = documentHtml.slice(start + BODY_START.length, end);
    if (SIGNATURE_WRAPPER_START.test(inner)) {
        inner = inner.replace(SIGNATURE_WRAPPER_START, '').replace(SIGNATURE_WRAPPER_END, '');
    }
    let after = documentHtml.slice(end);
    // Verlauf direkt hinter dem Text (rstours: Signatur erst darunter): seine Leerzeilen vor "Von:"
    // (replyHeader.js) gehören wie in Outlook zum Text – sonst sehen sie beschreibbar aus, liegen aber in der
    // gesperrten Vorschau
    const quoteGap = after.match(QUOTE_RIGHT_AFTER_BODY);
    if (quoteGap) {
        inner += quoteGap[2];
        after = quoteGap[1] + after.slice(quoteGap[0].length);
    }
    return {
        before: documentHtml.slice(0, start + BODY_START.length),
        body: inner,
        after,
    };
}

const QUOTE_RIGHT_AFTER_BODY = new RegExp('^(' + BODY_END + '(?:\\s|</(?:div|span|a|p)>)*<!-- emailQuoteStart -->)'
    + '((?:\\s*<p\\b[^>]*>(?:\\s|&nbsp;|<br\\s*/?>)*</p>)+)', 'i');

// Breite, in der der Text beim Empfänger umbricht: letzte feste Pixelbreite der Hülle vor dem Text
// (Buspaket/weTours/Welcome: <td width="560">). Ohne Hülle oder ohne feste Breite (Eurobin) → null, voll breit.
export function bodyWidthIn(parts) {
    if (!parts) {
        return null;
    }
    const widths = Array.from(parts.before.matchAll(/<t(?:d|able)\b[^>]*\swidth="?(\d+)"?[\s>]/gi));
    return widths.length ? parseInt(widths[widths.length - 1][1], 10) : null;
}

export function assembleEmailDocument(parts, bodyHtml) {
    return parts.before + bodyHtml + parts.after;
}

// Für die Vorschau unter dem Editor: alles außer dem bearbeitbaren Text (Signatur + angehängter Verlauf).
// Der Verlauf hängt hinter dem ersten </html> (clientSpecific/email/template/*/EmailBody.js) und wird
// eingepackt, damit "Signatur bearbeiten" ihn gesperrt lassen kann.
export const QUOTE_ATTRIBUTE = 'data-email-quote';

export const QUOTE_START = '<!-- emailQuoteStart -->';

// wo der Verlauf beginnt: Marker aus replyHeader.js, bei älteren Entwürfen hinter dem ersten </html>
function quoteStart(after) {
    const marker = after.indexOf(QUOTE_START);
    if (marker >= 0) {
        return {index: marker, skip: 0};
    }
    const end = after.indexOf('</html>');
    return end >= 0 ? {index: end, skip: '</html>'.length} : null;
}

export function documentWithoutBody(parts) {
    const quote = quoteStart(parts.after);
    if (!quote) {
        return parts.before + parts.after;
    }
    return parts.before + parts.after.slice(0, quote.index)
        + `<div ${QUOTE_ATTRIBUTE} contenteditable="false">` + parts.after.slice(quote.index + quote.skip) + '</div></html>';
}

// hat die Vorlage unter dem Text eine Signatur (sichtbarer Text oder Bild vor dem Verlauf)?
export function signatureIn(parts) {
    const quote = quoteStart(parts.after);
    const signature = quote ? parts.after.slice(0, quote.index) : parts.after;
    if (/<img\b/i.test(signature)) {
        return true;
    }
    return !!signature.replace(/<!--[\s\S]*?-->/g, '').replace(/<[^>]+>/g, '').replace(/&nbsp;|\s/g, '').trim();
}

// hängt eine Mail als Verlauf an (Antwort/Weiterleitung)?
export function quoteIn(parts) {
    const marker = parts.after.indexOf(QUOTE_START);
    if (marker < 0) {
        return false;
    }
    const quote = parts.after.slice(marker + QUOTE_START.length);
    return /<img\b/i.test(quote) || !!quote.replace(/<!--[\s\S]*?-->/g, '').replace(/<[^>]+>/g, '').replace(/&nbsp;|\s/g, '').trim();
}

// "Signatur/Verlauf bearbeiten": bearbeitetes Vorschau-Dokument (Signatur + Verlauf) wieder in before/after zerlegen.
// Die Verlaufs-Marke steht danach immer vor dem Verlauf – auch wenn sie beim Löschen im Verlauf mit entfernt wurde.
export function partsFromEditedAppendix(documentElement, body) {
    const copy = documentElement.cloneNode(true);
    const isQuoteMarker = node => node.nodeType === 8 && `<!--${node.data}-->` === QUOTE_START;
    copy.querySelectorAll(`[${QUOTE_ATTRIBUTE}]`).forEach(quote => {
        const walker = copy.ownerDocument.createTreeWalker(quote, 128 /* NodeFilter.SHOW_COMMENT */);
        const markers = [];
        while (walker.nextNode()) {
            if (isQuoteMarker(walker.currentNode)) {
                markers.push(walker.currentNode);
            }
        }
        markers.forEach(marker => marker.remove());
        quote.parentNode.insertBefore(copy.ownerDocument.createComment(QUOTE_START.slice(4, -3)), quote);
        while (quote.firstChild) {
            quote.parentNode.insertBefore(quote.firstChild, quote);
        }
        quote.remove();
    });
    // nur für die Vorschau (Links im neuen Tab, Ausrichtung am Editor-Text)
    copy.querySelectorAll('[data-email-preview]').forEach(element => element.remove());
    // ältere Fassung richtete die Vorschau direkt am <body> aus – das darf nicht in die Mail
    const mailBody = copy.querySelector('body');
    if (mailBody && mailBody.hasAttribute('data-aligned')) {
        mailBody.removeAttribute('data-aligned');
        mailBody.style.removeProperty('margin-top');
        mailBody.style.removeProperty('margin-left');
    }
    copy.querySelectorAll('[contenteditable]').forEach(el => {
        if (!el.hasAttribute('data-twig')) {
            el.removeAttribute('contenteditable');
        }
    });
    const parts = splitEmailDocument(copy.outerHTML);
    return parts ? {before: parts.before, body, after: parts.after} : null;
}
